import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { combineAll, WINDOW } from '@datachain/ui-sdk/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AccountRequester } from '@requesters/account.requester';
import { OrganizationRequester } from '@requesters/organization.requester';
import { catchError, combineLatest, map, of, switchMap, tap } from 'rxjs';

import {
	getDetailsForConnectedUser,
	getDetailsForConnectedUserFail,
	getDetailsForConnectedUserSuccess,
	navigateToMyAccount,
	setAppLanguage,
} from './app.actions';
import { AppSelector } from './app.selector';
import {
	ACCOUNT_PATH,
	MODULES_PATH,
	ROOT_ORGANIZATION_PATH,
} from '../../constants';
import { AppAdapter } from '../adapters/app.adapter';
import { Preferences } from '../domain/preferences';

@Injectable()
export class AppEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################
	public navigateToAccount$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToMyAccount),
				tap(() => this.router.navigate([ACCOUNT_PATH])),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				}),
			),
		{
			dispatch: false,
		},
	);

	public syncLangPref$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(setAppLanguage),
				tap((action) => {
					localStorage.setItem('pref:lang', action.lang);
					this.window.location.reload();
				}),
			),
		{
			dispatch: false,
		},
	);

	public navigateToFirstKnowOrganization$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(getDetailsForConnectedUserSuccess),
				tap((action) => {
					if (
						!this.window.location.href.includes(ROOT_ORGANIZATION_PATH) &&
						!this.window.location.href.includes(ACCOUNT_PATH)
					) {
						this.router.navigate([
							ROOT_ORGANIZATION_PATH,
							action.defaultOrganizationId,
							MODULES_PATH,
						]);
					}
				}),
			),
		{
			dispatch: false,
		},
	);

	public toggleGridSelection$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(getDetailsForConnectedUserSuccess),
				tap((action) => {
					this.appAdapter.updateStoredGridsConfig(action.features);
				}),
			),
		{
			dispatch: false,
		},
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public getConnectedUserDetails$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getDetailsForConnectedUser),
			switchMap(({ user }) =>
				combineAll({
					user: of(user),
					availableInstances:
						this.accountRequester.getAvailableInstanceModules(),
					organizationsForUser: combineLatest(
						[...user.accounts.keys()].map((orgId) =>
							this.organizationRequester.getOrganizationById(orgId),
						),
					),
					preferences: this.accountRequester.getPreferences(),
					features: this.accountRequester.getAvailableAppFeatures(),
				}),
			),
			map(
				({
					user,
					availableInstances,
					organizationsForUser,
					preferences,
					features,
				}) => {
					const selectedLanguage =
						this.appAdapter.languageMapping[preferences.language];
					const savedLanguage = localStorage.getItem('pref:lang');

					if (selectedLanguage !== savedLanguage) {
						return setAppLanguage({
							lang: selectedLanguage,
						});
					}

					return getDetailsForConnectedUserSuccess({
						defaultOrganizationId: user.primaryOrganizationId,
						instanceModules: this.appAdapter.generateAvailableInstanceModules(
							availableInstances,
							this.window.location,
						),
						organizationPerUserAccount:
							this.appAdapter.generateOrganizationPerUserAccount(
								organizationsForUser,
							),
						preferences: Preferences.build({
							language: selectedLanguage,
						}),
						features: this.appAdapter.generateFeatures(features),
					});
				},
			),
			catchError((error: HttpErrorResponse, caught) => {
				this.store.dispatch(
					getDetailsForConnectedUserFail({
						error,
					}),
				);
				return caught;
			}),
		),
	);
	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public constructor(
		private readonly actions$: Actions,
		private readonly store: Store,
		private readonly router: Router,
		private readonly appSelector: AppSelector,
		private readonly accountRequester: AccountRequester,
		private readonly organizationRequester: OrganizationRequester,
		private readonly appAdapter: AppAdapter,
		@Inject(WINDOW) private readonly window: Window,
	) {}

	private navigateToLoginPage(): void {
		// TODO: Remember current URL
		this.router.navigateByUrl('/');
	}
}
